/*
 * Routes that take turns (old Steward 01-core.js routeParity, 04-route.js routeData). Two routes on the same weekday whose
 * jobs come every 2 weeks run on alternate weeks, like Broadmoor and Westbank on Saturdays. A weekly job on one of them
 * is on both: each week it rides with whichever route runs that week, at its own place in line for that week (old
 * wkOrder), so each Saturday is one run in fuel-saving order (owner, Oct 30).
 */

/** Which of two alternating weeks a day is in (0 or 1), counted in whole weeks from Monday Jan 1 2024, like the old app. */
export function weekParity(date: string): 0 | 1 {
  const n = Math.floor((Date.parse(`${date}T12:00:00Z`) - Date.parse("2024-01-01T12:00:00Z")) / (7 * 86_400_000));
  return (((n % 2) + 2) % 2) as 0 | 1;
}

/** 1 Monday to 7 Sunday. */
const isoWeekday = (date: string): number => ((new Date(`${date}T12:00:00Z`).getUTCDay() + 6) % 7) + 1;

/**
 * A route's own week: the week most of its every-2-weeks jobs come on, or null when it has none (it runs every week).
 * A tie goes to week 0, as the old app did.
 */
export function routeWeek(jobs: readonly { frequency: string; startDate: string | null; active: boolean }[]): 0 | 1 | null {
  const n = [0, 0];
  for (const j of jobs) if (j.active && j.frequency === "biweekly" && j.startDate) n[weekParity(j.startDate)]! += 1;
  if (n[0] === n[1]) return n[0]! > 0 ? 0 : null;
  return n[0]! > n[1]! ? 0 : 1;
}

export interface TurnRoute {
  id: string;
  weekday: number;
  week: 0 | 1 | null;
}

/**
 * The route a job's visit runs with on a day. A weekly job whose route shares its weekday with another route, where the
 * routes take turns, goes with the one whose week the day is in. Anything else stays on its own route.
 */
export function routeOnDay(
  job: { routeId: string | null; frequency: string },
  date: string,
  routes: readonly TurnRoute[],
): string | null {
  if (!job.routeId || job.frequency !== "weekly") return job.routeId;
  const own = routes.find((r) => r.id === job.routeId);
  if (own?.weekday !== isoWeekday(date)) return job.routeId;
  const mates = routes.filter((r) => r.weekday === own.weekday && r.week !== null);
  if (mates.length < 2) return job.routeId;
  return mates.find((r) => r.week === weekParity(date))?.id ?? job.routeId;
}

/** True when the day is a turn day for this job: its weekly place for that week counts. */
export function takesTurns(job: { routeId: string | null; frequency: string }, date: string, routes: readonly TurnRoute[]) {
  if (!job.routeId || job.frequency !== "weekly") return false;
  const own = routes.find((r) => r.id === job.routeId);
  if (own?.weekday !== isoWeekday(date)) return false;
  return routes.filter((r) => r.weekday === own.weekday && r.week !== null).length >= 2;
}
